import React, { useEffect, useState } from "react";
import { AlertCircle, LogOut, Zap } from "lucide-react";
import { api, fmtTime } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useNavigate } from "react-router-dom";

function Dot({ state }) {
  const map = {
    OPEN: "live",
    CONNECTED: "live",
    LIVE: "live",
    PREOPEN: "warn",
    EXPIRED: "warn",
    STALE: "warn",
    ZOMBIE: "warn",
    HOLIDAY: "warn",
    DISCONNECTED: "down",
    CLOSED: "down",
    DEMO: "neutral",
  };
  return <span className={`status-dot ${map[state] || "down"}`} />;
}

import { statusBarLeftClass } from "@/lib/sidebarMode";

export default function StatusBar({ sidebarMode = "full", isMobile = false }) {
  const [market, setMarket] = useState(null);
  const [kite, setKite] = useState(null);
  const [worker, setWorker] = useState(null);
  const [dataHealth, setDataHealth] = useState(null);
  const [stopActive, setStopActive] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const refresh = async () => {
    try {
      const [m, k, w, dh, es] = await Promise.all([
        api.get("/market/state"),
        api.get("/zerodha/status"),
        api.get("/worker/status"),
        api.get("/data-health"),
        api.get("/emergency-stop"),
      ]);
      setMarket(m.data);
      setKite(k.data);
      setWorker(w.data);
      setDataHealth(dh.data);
      setStopActive(!!es.data.active);
    } catch (e) {
      // Silent by design — polled every 10s, transient network errors are
      // expected during backend restarts. Surface only if it persists via
      // the Data Health page.
      if (process.env.NODE_ENV === "development") {
        console.warn("StatusBar refresh failed:", e?.message);
      }
    }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 10000);
    return () => clearInterval(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleStop = async () => {
    try {
      if (stopActive) {
        // reset requires confirmation
        setConfirmOpen(true);
      } else {
        await api.post("/emergency-stop", { active: true, reason: "manual" });
        setStopActive(true);
        toast("Emergency stop activated. New signals & entries blocked.");
        refresh();
      }
    } catch (e) {
      toast.error("Failed to toggle emergency stop");
    }
  };

  const confirmReset = async () => {
    try {
      await api.post("/emergency-stop", {
        active: false,
        confirmation: confirmText,
      });
      setStopActive(false);
      setConfirmOpen(false);
      setConfirmText("");
      toast("Emergency stop reset.");
      refresh();
    } catch (e) {
      toast.error("Confirmation phrase incorrect");
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // Logout should always succeed client-side even if the server call
      // fails (e.g., token already expired). Log for dev only.
      if (process.env.NODE_ENV === "development") {
        console.warn("Logout API failed:", e?.message);
      }
    }
    // Session cookie is httpOnly — the backend's /auth/logout already
    // cleared it. Nothing to remove client-side.
    setUser(null);
    navigate("/login");
  };

  return (
    <>
      <header
        className={`fixed top-0 ${statusBarLeftClass(sidebarMode, isMobile)} right-0 border-b border-slate-800 bg-[#0d1421]/95 backdrop-blur z-20 flex items-center px-3 sm:px-4 gap-3 sm:gap-6 text-xs transition-[left] duration-150 overflow-x-auto status-bar-safe`}
        data-testid="status-bar"
      >
        <div className="flex items-center shrink-0" data-testid="market-state">
          <Dot state={market?.india_market_state} />
          <span className="uppercase tracking-[0.15em] text-slate-400 hidden sm:inline">Market:</span>
          <span className="ml-1 sm:ml-2 text-slate-100 font-mono">{market?.india_market_state || "…"}</span>
        </div>

        <div className="flex items-center shrink-0 hidden md:flex" data-testid="kite-state">
          <Dot state={kite?.state} />
          <span className="uppercase tracking-[0.15em] text-slate-400">Zerodha:</span>
          <span className="ml-2 text-slate-100 font-mono">{kite?.state || "…"}</span>
        </div>

        <div className="flex items-center shrink-0 hidden lg:flex" data-testid="worker-state">
          <Dot state={worker?.stale ? "STALE" : "LIVE"} />
          <span className="uppercase tracking-[0.15em] text-slate-400">Worker:</span>
          <span className="ml-2 text-slate-100 font-mono">
            {worker ? (worker.stale ? "STALE" : `T${worker.fencing_token}`) : "…"}
          </span>
        </div>

        <div className="flex items-center shrink-0" data-testid="stream-state">
          <Dot state={dataHealth?.stream_state} />
          <span className="uppercase tracking-[0.15em] text-slate-400 hidden sm:inline">Stream:</span>
          <span className="ml-1 sm:ml-2 text-slate-100 font-mono">{dataHealth?.stream_state || "…"}</span>
        </div>

        <div className="ml-auto flex items-center gap-2 sm:gap-4 shrink-0">
          <span
            className="px-2 py-1 rounded-sm bg-amber-400 text-black text-[10px] font-bold uppercase tracking-[0.2em] hidden sm:inline-block"
            data-testid="paper-mode-badge"
          >
            Paper Mode
          </span>

          <button
            data-testid="emergency-stop-btn"
            onClick={toggleStop}
            className={`px-2 sm:px-3 py-1.5 rounded-sm text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors shrink-0 ${
              stopActive
                ? "bg-amber-500 text-black hover:bg-amber-400"
                : "bg-red-600 text-white hover:bg-red-500"
            }`}
          >
            <AlertCircle size={12} className="inline mr-0 sm:mr-1" />
            <span className="hidden sm:inline">{stopActive ? "Reset Emergency" : "Emergency Stop"}</span>
            <span className="sm:hidden">{stopActive ? "Reset" : "Stop"}</span>
          </button>

          {user && (
            <div className="flex items-center gap-2 pl-2 sm:pl-4 sm:border-l border-slate-800">
              <div className="text-right hidden md:block">
                <div className="text-[10px] uppercase tracking-[0.15em] text-slate-500">
                  Signed in
                </div>
                <div className="text-slate-200 truncate max-w-[160px]">{user.name || user.email}</div>
              </div>
              <button
                data-testid="logout-btn"
                onClick={logout}
                className="p-2 border border-slate-800 rounded-sm hover:bg-slate-800/50"
                title="Log out"
              >
                <LogOut size={14} />
              </button>
            </div>
          )}
        </div>
      </header>

      {confirmOpen && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-md z-50 flex items-center justify-center p-4" data-testid="reset-confirm-modal">
          <div className="surface rounded-sm max-w-md w-full p-6">
            <div className="text-red-400 uppercase text-xs tracking-[0.2em] mb-2 flex items-center gap-2">
              <Zap size={14} /> Reset Emergency Stop
            </div>
            <p className="text-sm text-slate-300 mb-4">
              Type <code className="text-amber-400">RESET EMERGENCY STOP</code> exactly to confirm.
            </p>
            <input
              data-testid="reset-confirm-input"
              autoFocus
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              className="w-full bg-slate-900 border border-slate-700 rounded-sm px-3 py-2 text-sm font-mono text-slate-100 focus:border-blue-500 outline-none"
              placeholder="Confirmation phrase"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => {
                  setConfirmOpen(false);
                  setConfirmText("");
                }}
                className="px-4 py-2 rounded-sm border border-slate-700 text-slate-300 hover:bg-slate-800/50 text-xs uppercase tracking-[0.15em]"
              >
                Cancel
              </button>
              <button
                data-testid="reset-confirm-submit"
                onClick={confirmReset}
                className="px-4 py-2 rounded-sm bg-red-600 text-white hover:bg-red-500 text-xs uppercase tracking-[0.15em]"
              >
                Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
